import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip if we already have user data (already logged in via login function)
    if (user) {
      setLoading(false);
      return;
    }
    
    // Check if token has expired (for remember me feature)
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
      // Token expired, clear it
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('rememberMe');
      setToken(null);
      setLoading(false);
      return;
    }

    if (token) {
      authAPI.getCurrentUser()
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch((err) => {
          // Only clear token on 401 Unauthorized, not on network errors
          if (err.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('tokenExpiry');
            localStorage.removeItem('rememberMe');
            setToken(null);
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token, user]);

  const login = useCallback((newToken, userData, rememberMe = false) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setLoading(false); // Ensure loading is false immediately after login
    
    if (rememberMe) {
      // Set expiry to 3 days from now
      const expiryTime = new Date().getTime() + (3 * 24 * 60 * 60 * 1000);
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      localStorage.setItem('rememberMe', 'true');
    } else {
      // Session only - will expire when browser closes (handled by sessionStorage behavior)
      // But since we use localStorage, we'll set a short expiry (1 hour for session)
      const expiryTime = new Date().getTime() + (1 * 60 * 60 * 1000);
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      localStorage.removeItem('rememberMe');
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    setToken(null);
    setUser(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user, token, login, logout, loading, updateUser
  }), [user, token, login, logout, loading, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};